import { useMemo } from "react";

// Bounding box for Southern Africa
const LAT_TOP = -14, LAT_BOTTOM = -35.5, LNG_LEFT = 15, LNG_RIGHT = 34;
const W = 800, H = 760;

function project(lat, lng) {
  const x = ((lng - LNG_LEFT) / (LNG_RIGHT - LNG_LEFT)) * W;
  const y = ((LAT_TOP - lat) / (LAT_TOP - LAT_BOTTOM)) * H;
  return [x, y];
}

const STATUS_COLORS = {
  normal: "#16A34A", at_risk: "#EAB308", delayed: "#F97316",
  exception: "#DC2626", delivered: "#2563EB",
};
const STATUS_OF = {
  "In Transit": "normal", "Pending Pickup": "normal", "At Risk": "at_risk",
  "Delayed": "delayed", "Exception": "exception", "Delivered": "delivered",
};

// Stylised Southern Africa landmass (abstract, for visual context)
const LAND_PATH =
  "M150,120 C230,90 330,95 420,110 C500,124 560,120 620,150 C660,170 675,210 660,260 C648,305 665,340 640,390 C618,435 600,470 560,520 C520,570 470,610 420,650 C380,680 340,700 300,680 C260,662 250,610 230,560 C210,510 175,485 165,430 C156,382 135,360 140,300 C144,250 115,175 150,120 Z";

export default function SouthernAfricaMap({ shipments = [], cities = {}, height = 520, onSelect, routeStops = [], routeGeometry = [], testid = "sa-map" }) {
  const cityPts = useMemo(
    () => Object.entries(cities).map(([name, [lat, lng]]) => ({ name, xy: project(lat, lng) })),
    [cities]
  );

  const routeNodes = useMemo(
    () => routeStops.map((s) => (cities[s.city] ? { ...s, xy: project(cities[s.city][0], cities[s.city][1]) } : null)).filter(Boolean),
    [routeStops, cities]
  );
  const geomPts = useMemo(
    () => (routeGeometry.length > 1 ? routeGeometry.map(([lat, lng]) => project(lat, lng)) : routeNodes.map((n) => n.xy)),
    [routeGeometry, routeNodes]
  );
  const routeD = geomPts.length > 1 ? geomPts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ") : "";
  const fmtEta = (t) => (t ? new Date(t).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" }) : "");

  return (
    <div className="relative w-full overflow-hidden rounded-xl bg-[#0B0F17] grain" style={{ height }} data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="glow" cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0B0F17" />
          </radialGradient>
          <linearGradient id="land" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#16213a" />
            <stop offset="100%" stopColor="#0f1a2e" />
          </linearGradient>
        </defs>
        <rect width={W} height={H} fill="url(#glow)" />
        {/* grid */}
        {Array.from({ length: 9 }).map((_, i) => (
          <line key={"v" + i} x1={(i * W) / 8} y1="0" x2={(i * W) / 8} y2={H} stroke="#1e293b" strokeWidth="0.5" opacity="0.4" />
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <line key={"h" + i} x1="0" y1={(i * H) / 8} x2={W} y2={(i * H) / 8} stroke="#1e293b" strokeWidth="0.5" opacity="0.4" />
        ))}
        <path d={LAND_PATH} fill="url(#land)" stroke="#DC2626" strokeWidth="1.2" strokeOpacity="0.35" />

        {/* routes */}
        {shipments.filter((s) => s.status !== "Delivered").slice(0, 30).map((s, i) => {
          const o = cities[s.origin], d = cities[s.destination];
          if (!o || !d) return null;
          const [x1, y1] = project(o[0], o[1]);
          const [x2, y2] = project(d[0], d[1]);
          const mx = (x1 + x2) / 2, my = (y1 + y2) / 2 - 40;
          return (
            <path key={"r" + i} d={`M${x1},${y1} Q${mx},${my} ${x2},${y2}`} fill="none"
              stroke={STATUS_COLORS[STATUS_OF[s.status] || "normal"]} strokeWidth="1.5"
              strokeOpacity="0.5" className="route-line" />
          );
        })}

        {/* city nodes */}
        {cityPts.map((c) => (
          <g key={c.name}>
            <circle cx={c.xy[0]} cy={c.xy[1]} r="4.5" fill="#fff" />
            <circle cx={c.xy[0]} cy={c.xy[1]} r="9" fill="none" stroke="#DC2626" strokeWidth="1" opacity="0.6" />
            <text x={c.xy[0] + 12} y={c.xy[1] + 4} fill="#cbd5e1" fontSize="12" fontFamily="JetBrains Mono, monospace">
              {c.name}
            </text>
          </g>
        ))}

        {/* optimised route overlay */}
        {routeD && (
          <g data-testid="route-overlay">
            <path d={routeD} fill="none" stroke="#DC2626" strokeWidth="4" strokeOpacity="0.28" strokeLinecap="round" strokeLinejoin="round" />
            <path d={routeD} fill="none" stroke="#fca5a5" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray="10 14">
              <animate attributeName="stroke-dashoffset" from="48" to="0" dur="1.1s" repeatCount="indefinite" />
            </path>
            {routeNodes.map((n, i) => (
              <g key={"rn" + i}>
                <circle cx={n.xy[0]} cy={n.xy[1]} r="12" fill={i === 0 ? "#0B0F17" : (n.breach ? "#F97316" : "#DC2626")} stroke="#fff" strokeWidth="2" />
                <text x={n.xy[0]} y={n.xy[1] + 4} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700"
                  fontFamily="JetBrains Mono, monospace">{i === 0 ? "S" : i}</text>
                {n.eta && (
                  <text x={n.xy[0]} y={n.xy[1] + 26} textAnchor="middle" fill="#fca5a5" fontSize="11" fontWeight="700"
                    fontFamily="JetBrains Mono, monospace">{fmtEta(n.eta)}</text>
                )}
              </g>
            ))}
          </g>
        )}

        {/* live vehicle nodes */}
        {shipments.filter((s) => s.status !== "Delivered" && s.current_lat).slice(0, 30).map((s, i) => {
          const [x, y] = project(s.current_lat, s.current_lng);
          const color = STATUS_COLORS[STATUS_OF[s.status] || "normal"];
          return (
            <g key={"v" + i} style={{ cursor: onSelect ? "pointer" : "default" }} onClick={() => onSelect && onSelect(s)}>
              <circle cx={x} cy={y} r="5" fill={color} stroke="#fff" strokeWidth="1.5">
                <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}
      </svg>
      <div className="absolute top-4 left-4 flex flex-wrap gap-3 text-[11px] font-mono text-slate-300">
        {Object.entries(STATUS_COLORS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: v }} />
            {k.replace("_", " ")}
          </span>
        ))}
      </div>
    </div>
  );
}
